import { ScheduleSelectorTop } from "slot-definitions"
import { PlanRoot } from "library/tree/constants.mjs"
import { registerPublishingStep } from "routes/regime/plugins/publishing/register-publishing-step"
import { parentFind } from "library/tree/index.mjs"
import { getBasePlanId } from "routes/plan/controller/use-plan"
import { If } from "lib/@components/switch"
import { CreateDraft } from "routes/plan/create-draft"
import { UpdateDraft } from "routes/plan/update-draft"
import { navigate } from "lib/routes/navigate"
import { uniqueId } from "library/database/split-id.mjs"
import { createPortalPair } from "lib/portals"
import { available } from "routes/regime/plugins/publishing/available"
import { PublishingSteps } from "routes/regime/plugins/publishing/publishing-steps"
import { PublishButton } from "routes/regime/plugins/publishing/publish-button"
import { PublishArea } from "routes/regime/plugins/publishing/publish-area"
import { RejectReviewButton } from "routes/regime/plugins/publishing/reject-review-button"
import { Feature } from "lib/@components/feature"
import { FEATURE_FLAG_REGIME_AUDITING } from "../regime-auditing/server/feature-flag-regime-auditing.mjs"
import { TailoringRejectionChecker } from "routes/asset-register/plugins/general-tailoring/plugins/tailored-tab-auditing/plugins/tailoring-rejection/tailoring-rejection-checker"
import { RegimeDraftLockHide } from "routes/regime/regime-draft-locking/regime-draft-lock-hide"

// Hidden for share-link visitors: the publishing stage workflow is not relevant to them
ScheduleSelectorTop("*").plug(<PublishingSteps if={(c) => !!c.parentItem?.[PlanRoot] && !c.isShare} priority={1} />)

export const [PublishPortal, PublishClient] = createPortalPair("Publish")
export const [DraftPortal, DraftClient] = createPortalPair("Draft")
export const [LivePortal, LiveClient] = createPortalPair("Live")

registerPublishingStep({
    title: "Draft",
    icon: "ph:compass-tool-duotone",
    available: (...params) => available("")(...params) ?? true,
    isSelected() {
        return window.location.pathname.includes("/app/schedules")
    },
    onClick({ parentItem }) {
        const parent = parentFind(parentItem, (c) => c[PlanRoot])
        const id = getBasePlanId(parent)
        navigate(`/app/schedules?id=${uniqueId(id)}`, { replace: true })
    },
    content: <DraftPortal width={1} minHeight={40} />,
})

registerPublishingStep({
    title: "Review",
    icon: "carbon:test-tool",
    available: available("publish"),
    content: (
        <>
            <Feature feature={FEATURE_FLAG_REGIME_AUDITING}>
                <RejectReviewButton />
            </Feature>
            <TailoringRejectionChecker
                step="Review"
                message="You can't approve the review while there are unresolved rejections. Please resolve them to continue."
            >
                <PublishButton />
            </TailoringRejectionChecker>

            <If field="activeStep.props.id" equals="Draft">
                <DraftClient>
                    <If
                        field="isAvailable"
                        then={<UpdateDraft />}
                        else={
                            <RegimeDraftLockHide>
                                <TailoringRejectionChecker
                                    step="Draft"
                                    message="You can't submit for review while there are unresolved rejections. Please resolve them to continue."
                                >
                                    <CreateDraft />
                                </TailoringRejectionChecker>
                            </RegimeDraftLockHide>
                        }
                    />
                </DraftClient>
            </If>
            <PublishArea />
        </>
    ),
    isSelected() {
        return window.location.pathname.includes("/app/review/")
    },
    onClick({ parentItem }) {
        const parent = parentFind(parentItem, (c) => c[PlanRoot])
        const id = getBasePlanId(parent)
        navigate(`/app/review/${uniqueId(id)}`)
    },
})

registerPublishingStep({
    title: "Candidate",
    icon: "mingcute:tool-line",
    available: available("ready"),
    isSelected() {
        return window.location.href.includes("/app/schedules?id=ready")
    },
    content: <LivePortal width={1} />,
    onClick({ parentItem }) {
        const parent = parentFind(parentItem, (c) => c[PlanRoot])
        const id = getBasePlanId(parent)
        navigate(`/app/schedules?id=ready|${uniqueId(id)}`, { replace: true })
    },
})

registerPublishingStep({
    title: "Live",
    icon: "mingcute:tool-fill",
    available: available("live"),

    isSelected() {
        return window.location.href.includes("/app/schedules?id=live")
    },
    onClick({ parentItem }) {
        const parent = parentFind(parentItem, (c) => c[PlanRoot])
        const id = getBasePlanId(parent)
        navigate(`/app/schedules?id=live|${uniqueId(id)}`, { replace: true })
    },
})
